import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'

/**
 * Resolves the value emitted by CompanyCombobox (an existing company id or a
 * typed new-company name) into a real company id. A brand-new company is
 * created only when explicitly requested at save time, and can be rolled back
 * if the parent save fails.
 */
export function useCompanyResolve() {
  const planningStore = usePlanningStore()

  const resolveCompany = async(value) => {
    if (value === null || value === undefined || value === '') return { id: null, createdId: null }
    if (typeof value !== 'string') return { id: value, createdId: null }

    const name = value.trim()
    if (!name) return { id: null, createdId: null }

    const existing = planningStore.companies.find(c => c.name?.toLowerCase() === name.toLowerCase())
    if (existing) return { id: existing.id, createdId: null }

    const code = String(await planningStore.fetchNextCompanyIdAsync())
    const { data } = await planningStore.upsertCompanyAsync({ name, code })
    await planningStore.fetchCompaniesAsync({}, true)
    return { id: data.id, createdId: data.id }
  }

  const rollbackCompany = async(createdId) => {
    if (createdId) await planningStore.deleteCompanyAsync(createdId)
  }

  return { resolveCompany, rollbackCompany }
}

export function emptyContactReferences() {
  const { referenceTypeEnum } = usePlanningStore()
  return { [referenceTypeEnum.PHONE]: '', [referenceTypeEnum.EMAIL]: '' }
}

export function useContactReferences() {
  const contactReferences = ref(emptyContactReferences())
  const { isUpdated: contactReferencesChanged, setOriginal } = useIsUpdated(contactReferences)

  const loadContactReferences = (references) => {
    contactReferences.value = references
    setOriginal(references)
  }

  return { contactReferences, contactReferencesChanged, loadContactReferences }
}

export function useContactResolve() {
  const planningStore = usePlanningStore()
  const { contactResolveResultEnum } = planningStore
  const { notifyInfo } = useNotify()
  const i18n = getI18n()

  const typedReferences = (contactReferences) => Object.entries(contactReferences)
    .map(([referenceType, value]) => ({ reference_type: Number(referenceType), value: value?.trim(), is_default: true }))
    .filter(reference => reference.value)

  const defaultReference = (references, referenceType) => {
    const ofType = references.filter(r => r.reference_type === referenceType)
    return ofType.find(r => r.is_default) ?? ofType[0]
  }

  const unchanged = (id) => ({ id, result: contactResolveResultEnum.UNCHANGED, rollback: async() => {} })

  const createContact = async(companyId, name, contactReferences) => {
    const { data } = await planningStore.upsertContactAsync({ company: companyId, name, references: typedReferences(contactReferences) })
    return {
      id: data.id,
      result: contactResolveResultEnum.CREATED,
      rollback: () => planningStore.deleteContactAsync(data.id),
    }
  }

  const updateContactReferences = async(contactId, contactReferences) => {
    const { data: contact } = await planningStore.fetchContactByIdAsync(contactId)
    const references = contact.references.map(r => ({ ...r }))
    const changes = typedReferences(contactReferences)
      .filter(typed => defaultReference(references, typed.reference_type)?.value !== typed.value)
    if (!changes.length) return unchanged(contactId)

    changes.forEach((typed) => {
      const current = defaultReference(references, typed.reference_type)
      if (current) current.value = typed.value
      else references.push(typed)
    })
    await planningStore.upsertContactAsync({ id: contactId, references })
    return {
      id: contactId,
      result: contactResolveResultEnum.UPDATED,
      rollback: () => planningStore.upsertContactAsync({ id: contactId, references: contact.references }),
    }
  }

  const resolveContact = async(companyId, value, contactReferences) => {
    if (typeof value === 'number') return updateContactReferences(value, contactReferences)
    const name = value?.trim()
    if (!name || !companyId) return unchanged(null)
    return createContact(companyId, name, contactReferences)
  }

  const notifyContactResolved = (result) => {
    if (result === contactResolveResultEnum.CREATED) notifyInfo(i18n.t('info.contactSavedToCompany'))
    else if (result === contactResolveResultEnum.UPDATED) notifyInfo(i18n.t('info.contactUpdatedInCompany'))
  }

  return { resolveContact, notifyContactResolved }
}

export function useListingCompany(item) {
  const planningStore = usePlanningStore()

  const listingCompanyId = (listing) => {
    const id = typeof listing === 'object' ? listing?.id : listing
    return planningStore.getListings().find(l => l.id === id)?.company ?? null
  }

  const companyReadonly = computed(() => !!listingCompanyId(item.value.listing))

  const setCompany = (company) => {
    if (company !== item.value.company) item.value.contact = null
    item.value.company = company
  }

  const applyListingCompany = () => {
    const companyId = listingCompanyId(item.value.listing)
    if (companyId) setCompany(companyId)
  }

  return { companyReadonly, setCompany, applyListingCompany }
}
